import React from 'react';
import { Button, Box, Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Check, EyeOff, ChevronDown, CircleCheck, CircleX } from 'lucide-react';

// Color constants for access status
export const ACCESS_STATUS_COLORS = {
  access: {
    bg: 'rgba(34, 197, 94, 0.1)',
    border: 'rgba(34, 197, 94, 0.3)',
    text: 'rgba(34, 197, 94, 0.8)',
    icon: 'rgb(34, 197, 94)',
    selectedBg: 'rgb(45, 156, 132)', // Teal for selected state
  },
  pass: {
    bg: 'rgba(234, 179, 8, 0.1)',
    border: 'rgba(234, 179, 8, 0.3)',
    text: 'rgba(234, 179, 8, 0.8)',
    icon: 'rgb(234, 179, 8)',
    selectedBg: 'rgb(234, 179, 8)',
  },
  kill: {
    bg: 'rgba(239, 68, 68, 0.1)',
    border: 'rgba(239, 68, 68, 0.3)',
    text: 'rgba(239, 68, 68, 0.8)',
    icon: 'rgb(239, 68, 68)',
    selectedBg: 'rgb(239, 68, 68)',
  },
};

export type AccessStatus = 'access' | 'pass' | 'kill';

// Styled AccessButton component
export const AccessButton = styled(Button)<{ status: AccessStatus }>(({ status }) => ({
  width: 100,
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 6,
  padding: '4px 8px',
  gap: 4,
  minHeight: '30px',
  fontSize: '12px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: 'none',
  backgroundColor: ACCESS_STATUS_COLORS[status].bg,
  border: `1px solid ${ACCESS_STATUS_COLORS[status].border}`,
  color: ACCESS_STATUS_COLORS[status].text,
}));

// Get status icon with proper colors
export const getStatusIcon = (status: AccessStatus, size: number = 12) => {
  const color = ACCESS_STATUS_COLORS[status].icon;
  
  switch (status) {
    case 'access':
      return <CircleCheck size={size} style={{ color }} />;
    case 'pass':
      return <EyeOff size={size} style={{ color }} />;
    case 'kill':
      return <CircleX size={size} style={{ color }} />;
  }
};

// Props for AccessStatusDropdown
interface AccessStatusDropdownProps {
  status: AccessStatus;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (status: AccessStatus) => void;
  allowedStatuses?: AccessStatus[]; // Optional prop to restrict available statuses
}

// Get icon component for status
const getMenuIcon = (value: AccessStatus, size: number = 16) => {
  switch (value) {
    case 'access':
      return <CircleCheck size={size} />;
    case 'pass':
      return <EyeOff size={size} />;
    case 'kill':
      return <CircleX size={size} />;
  }
};

// Dropdown menu for selecting access status
export const AccessStatusDropdown: React.FC<AccessStatusDropdownProps> = ({
  status,
  anchorEl,
  onClose,
  onSelect,
  allowedStatuses,
}) => {
  const allMenuItems: { value: AccessStatus; label: string }[] = [
    { value: 'access', label: 'ACCESS' },
    { value: 'pass', label: 'PASS' },
    { value: 'kill', label: 'KILL' },
  ];
  
  // Filter menu items based on allowedStatuses if provided
  const menuItems = allowedStatuses 
    ? allMenuItems.filter(item => allowedStatuses.includes(item.value))
    : allMenuItems;

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
          minWidth: 140,
          overflow: 'hidden',
          p: 0.5,
          '&:hover': { bgcolor: 'background.paper' },
        },
      }}
    >
      {menuItems.map((item) => {
        const isSelected = status === item.value;
        
        return (
          <MenuItem
            key={item.value}
            onClick={() => {
              onSelect(item.value);
              onClose();
            }}
            sx={{
              padding: '8px 12px',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: 1.5,
              mb: 0.5,
              backgroundColor: isSelected ? 'primary.main' : 'transparent',
              color: isSelected ? 'white' : 'text.primary',
              '&:last-child': {
                mb: 0,
              },
              '&:hover': {
                backgroundColor: isSelected 
                  ? 'primary.main' 
                  : 'action.hover',
              },
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              width: '100%',
              gap: 1,
            }}>
              {/* Checkmark on left for selected item */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                width: 16,
              }}>
                {isSelected && <Check size={16} />}
              </Box>
              
              {/* Icon + Label */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
              }}>
                {getMenuIcon(item.value)}
                <span>{item.label}</span>
              </Box>
            </Box>
          </MenuItem>
        );
      })}
    </Menu>
  );
};

// Props for AccessStatusCell
interface AccessStatusCellProps {
  status: AccessStatus;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

// Cell component with button
export const AccessStatusCell: React.FC<AccessStatusCellProps> = ({ status, onClick }) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <AccessButton status={status} onClick={onClick}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getStatusIcon(status)}
          {status.toUpperCase()}
        </Box>
        <ChevronDown size={14} />
      </AccessButton>
    </Box>
  );
};
